import { Pencil, Plus, RotateCw, Search } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Row,
  Table
} from 'react-bootstrap'
import Select from 'react-select'
import { useNavigate } from 'react-router-dom'
import { useDebounce } from 'use-debounce'
import api from '../../../../config/AxiosInstance'
import '../../../../styles/style.css'
import Loader from '../../../atoms/Loader'
import TableAction from '../../../atoms/Table/TableAction'
import TablePagination from '../../../atoms/Table/TablePagination'
import toast, { Toaster } from 'react-hot-toast'

const cardFilterStyle = {
  backgroundColor: '#fff',
  borderRadius: '12px',
  border: 'none',
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  padding: '1.5rem',
  marginBottom: '1.5rem'
}

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: '700',
  color: '#333333',
  textTransform: 'uppercase',
  marginBottom: '0.5rem',
  letterSpacing: '0.5px'
}

const tableHeaderStyle = {
  fontSize: '14px',
  fontWeight: '600',
  color: '#333333',
  letterSpacing: '0.5px',
  backgroundColor: '#f8f9fa',
  borderBottom: '2px solid #e9ecef'
}

const FormControlStyle = {
  height: '45px',
  borderRadius: '10px',
  border: '1px solid #e9ecef',
  backgroundColor: '#ffffff',
  fontSize: '0.9rem'
}

function ManageRolePage() {
  const navigate = useNavigate()
  const [filterName, setFilterName] = useState('')
  const [filterRole, setFilterRole] = useState('')
  const [filterBranch, setFilterBranch] = useState(null)
  const [filterWarehouse, setFilterWarehouse] = useState(null)

  const [debouncedName] = useDebounce(filterName, 1000)
  const [debouncedRole] = useDebounce(filterRole, 1000)

  const [optionBranch, setOptionBranch] = useState([])
  const [inputBranch, setInputBranch] = useState('')
  const [optionWarehouses, setOptionWarehouses] = useState([])
  const [inputWarehouse, setInputWarehouse] = useState('')

  const [isLoading, setIsLoading] = useState(false)
  const [manageRoles, setManageRoles] = useState([])
  const [meta, setMeta] = useState({
    per_page: 10,
    current_page: 1,
    total_row: 0,
    total_page: 0
  })
  const { per_page, current_page } = meta

  const headerTitleStyle = {
    color: '#343a40',
    fontSize: '24px',
    fontWeight: '700'
  }

  useEffect(() => {
    const controller = new AbortController()
    fetchBranch(inputBranch, controller)
    return () => controller.abort()
  }, [inputBranch])

  useEffect(() => {
    const controller = new AbortController()
    fetchWarehouses(inputWarehouse, controller)
    return () => controller.abort()
  }, [inputWarehouse])

  const fetchManageRoles = useCallback(async () => {
    setIsLoading(true)
    try {
      const res = await api.get('cms/manage-role/list', {
        params: {
          name: debouncedName,
          role: debouncedRole,
          location_id: filterBranch?.value,
          warehouse_id: filterWarehouse?.value,
          per_page,
          page: current_page
        }
      })
      if (res.data.success) {
        const { metadata, data } = res.data
        setManageRoles(data)
        setMeta(metadata)
      }
    } catch (err) {
      setIsLoading(false)
      if (err.message !== 'canceled') {
        const errMsg =
          err?.response?.data?.errors?.message || 'An error occurred'
        toast.error(errMsg, {
          position: 'top-right',
          style: {
            fontSize: '14px',
            fontWeight: '600'
          }
        })
      }
    } finally {
      setIsLoading(false)
    }
  }, [
    debouncedName,
    debouncedRole,
    filterBranch,
    filterWarehouse,
    per_page,
    current_page
  ])

  useEffect(() => {
    if (meta.current_page !== 1) {
      setMeta((prev) => ({ ...prev, current_page: 1 }))
    } else {
      fetchManageRoles()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedName,
    debouncedRole,
    filterBranch,
    filterWarehouse,
    meta.per_page
  ])

  useEffect(() => {
    fetchManageRoles()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta.current_page])

  const fetchBranch = async (search, controller) => {
    try {
      const res = await api.get(`master/locations?q=${search}`, {
        signal: controller.signal
      })
      const optionValue = []
      if (res.data.data.length > 0) {
        res.data.data.map((area) =>
          optionValue.push({
            value: area.id,
            label: area.area_name
          })
        )
      }
      setOptionBranch(optionValue)
    } catch (err) {
      if (err.message !== 'canceled') {
        const errMsg =
          err?.response?.data?.errors?.message || 'An error occurred'
        toast.error(errMsg, {
          position: 'top-right',
          style: {
            fontSize: '14px',
            fontWeight: '600'
          }
        })
        console.error(errMsg)
      }
    }
  }

  const fetchWarehouses = async (search, controller) => {
    try {
      const res = await api.get(
        `master/warehouses/all?q=${search}&is_parent=true&forUser=1`,
        {
          signal: controller.signal
        }
      )
      const optionValue = []
      if (res.data.data.length > 0) {
        res.data.data.map((wh) =>
          optionValue.push({
            value: wh.id,
            label: wh.warehouse_name
          })
        )
      }
      setOptionWarehouses(optionValue)
    } catch (err) {
      if (err.message !== 'canceled') {
        const errMsg =
          err?.response?.data?.errors?.message || 'An error occurred'
        console.error(errMsg)
      }
    }
  }

  function handleOnChangeMeta(e) {
    const perPageValue = parseInt(e.target.value, 10)
    setMeta((prevMeta) => ({
      ...prevMeta,
      per_page: perPageValue,
      current_page: 1
    }))
  }

  const handlePageChange = (data) => {
    const selectedPage = data.selected + 1
    setMeta((prevMeta) => ({ ...prevMeta, current_page: selectedPage }))
    window.scrollTo(0, 0)
  }

  function handleEdit(id) {
    navigate(`/manage/role/edit/${id}`)
  }

  const resetFilter = () => {
    setFilterName('')
    setFilterRole('')
    setFilterBranch(null)
    setFilterWarehouse(null)
    setMeta((prev) => ({ ...prev, current_page: 1 }))
  }

  const columns = [
    { label: 'Name', width: '25%' },
    { label: 'Role Code', width: '' },
    { label: 'Role Name', width: '' },
    { label: 'Branch', width: '' },
    { label: 'Warehouse', width: '' },
    { label: 'Action', width: '' }
  ]

  return (
    <div className="container-fluid px-4 mb-5">
      <Row className="mb-4 align-items-center">
        <Col>
          <h3 style={headerTitleStyle}>Manage User Role</h3>
        </Col>
        <Col className="text-end">
          <Button
            variant="success"
            className="btn-primary-orange rounded px-3 me-2"
            onClick={() => navigate('/manage/role/create')}
            style={{ fontSize: '0.85rem' }}
          >
            <Plus size={16} className="me-2" />
            Add New
          </Button>
          <Button
            variant="light"
            className="text-secondary fw-bold rounded px-3 border"
            onClick={resetFilter}
            style={{ fontSize: '0.85rem' }}
          >
            <RotateCw size={16} className="me-2" />
            Reset Filters
          </Button>
        </Col>
      </Row>

      <Card style={cardFilterStyle}>
        <Row className="gy-3">
          <Col md={3}>
            <Form.Group>
              <Form.Label style={labelStyle}>Search Name</Form.Label>
              <InputGroup>
                <InputGroup.Text
                  style={{
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e9ecef',
                    borderRight: 'none',
                    borderTopLeftRadius: '10px',
                    borderBottomLeftRadius: '10px'
                  }}
                >
                  <Search size={16} className="text-muted" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  value={filterName}
                  onChange={(e) => setFilterName(e.target.value)}
                  placeholder="Name..."
                  style={{
                    ...FormControlStyle,
                    borderLeft: 'none',
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0
                  }}
                />
              </InputGroup>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label style={labelStyle}>Role</Form.Label>
              <InputGroup>
                <InputGroup.Text
                  style={{
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e9ecef',
                    borderRight: 'none',
                    borderTopLeftRadius: '10px',
                    borderBottomLeftRadius: '10px'
                  }}
                >
                  <Search size={16} className="text-muted" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  value={filterRole}
                  onChange={(e) => setFilterRole(e.target.value)}
                  placeholder="Search role name or code..."
                  style={{
                    ...FormControlStyle,
                    borderLeft: 'none',
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0
                  }}
                />
              </InputGroup>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label style={labelStyle}>Search Branch</Form.Label>
              <Select
                id="filterBranch"
                name="filterBranch"
                options={optionBranch}
                onInputChange={(val) => setInputBranch(val)}
                onChange={(val) => setFilterBranch(val)}
                value={filterBranch}
                placeholder="Branch..."
                isClearable
                isSearchable
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: '10px',
                    border: '1px solid #e9ecef',
                    height: '45px'
                  })
                }}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label style={labelStyle}>Search Warehouse</Form.Label>
              <Select
                id="filterWarehouse"
                name="filterWarehouse"
                options={optionWarehouses}
                onInputChange={(val) => setInputWarehouse(val)}
                onChange={(val) => setFilterWarehouse(val)}
                value={filterWarehouse}
                placeholder="Warehouse..."
                isClearable
                isSearchable
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: '10px',
                    border: '1px solid #e9ecef',
                    height: '45px'
                  })
                }}
              />
            </Form.Group>
          </Col>
        </Row>
      </Card>

      <Row>
        <Col md={12}>
          <Table
            responsive
            hover
            className="shadow-sm bg-white"
            style={{
              fontSize: '0.9rem',
              borderRadius: '12px',
              overflow: 'hidden'
            }}
          >
            <thead style={{ backgroundColor: '#f8f9fa' }}>
              <tr>
                {columns.map((col, idx) => (
                  <th
                    key={idx}
                    className="p-2 align-middle"
                    style={tableHeaderStyle}
                    width={col.width}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={6} className="p-5 text-center">
                    <Loader />
                  </td>
                </tr>
              )}
              {!isLoading && manageRoles.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="p-5 text-center text-muted fw-bold"
                  >
                    No Data Available
                  </td>
                </tr>
              )}
              {!isLoading &&
                manageRoles.length > 0 &&
                manageRoles?.map((manageRole) => (
                  <TbodyRow
                    data={manageRole}
                    key={manageRole.user_id}
                    handleEdit={() => handleEdit(manageRole.user_id)}
                  />
                ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={6} className="p-3 border-top-0">
                  <TablePagination
                    meta={meta}
                    handlePageChange={handlePageChange}
                    handleOnChangeMeta={handleOnChangeMeta}
                  />
                </td>
              </tr>
            </tfoot>
          </Table>
        </Col>
      </Row>

      <Toaster />
    </div>
  )
}

function TbodyRow({ data, handleEdit }) {
  return (
    <tr key={data.user_id} style={{ borderBottom: '1px solid #f0f0f0' }}>
      <td className="p-2 align-middle">
        {data?.user?.name || data?.name || data?.username}
      </td>
      <td className="p-2 align-middle">
        {data?.role?.role_code || data?.role_code || '-'}
      </td>
      <td className="p-2 align-middle">
        {data?.role?.role_name || data?.role_name || '-'}
      </td>
      <td className="p-2 align-middle">
        {data?.locations?.length > 0
          ? data.locations.map((loc) => (
              <Badge
                key={loc.id}
                bg="secondary"
                pill
                className="me-1 mb-1"
                style={{ fontWeight: '500' }}
              >
                {loc.area_name}
              </Badge>
            ))
          : data?.location?.area_name || '-'}
      </td>
      <td className="p-2 align-middle">
        {data?.warehouses?.length > 0
          ? data.warehouses.map((wh) => (
              <Badge
                key={wh.id}
                bg="secondary"
                pill
                className="me-1 mb-1"
                style={{ fontWeight: '500' }}
              >
                {wh.warehouse_name}
              </Badge>
            ))
          : data?.warehouse?.warehouse_name || '-'}
      </td>
      <td className="p-2 align-middle">
        <TableAction>
          <button
            className="btn btn-secondary border rounded p-2"
            onClick={() => handleEdit(data.user_id)}
            style={{ width: '35px', height: '35px' }}
          >
            <Pencil size={14} />
          </button>
        </TableAction>
      </td>
    </tr>
  )
}
export default ManageRolePage
